import { useStyles } from './styles'
import { $userLanguage, $userToken } from '@/entities/user'
import { lang } from '@/shared/constants/lang'
import { useUnit } from 'effector-react'
import { Icon } from '@/shared/ui/icon'
import { Spiner } from '@/shared/ui/spiner'
import ButtonWrapper from '@/shared/ui/buttonWrapper'
import { AppButton } from '@/shared/ui/button'
import { useHideNav } from '@/shared/hooks/useHideNav'
import { goToMain } from '@/app/routing/events'
import { getTransactionStatusFx, TransactionStatus } from '@/entities/payments'
import { $transactionHash } from '@/entities/navigation'
import { useEffect, useState, useCallback } from 'react'
import { padZero } from '@/shared/util/padZero'

export const TransactionStatusView = () => {
  useHideNav()
  const { classes } = useStyles()
  const userLang = useUnit($userLanguage)
  const texts = lang[userLang].pages.transactionStatus
  const btnTexts = lang[userLang].pages.btnTexts

  const [status, setStatus] = useState<TransactionStatus>('PENDING')
  const [isFetching, setIsFetching] = useState(false)
  const [countdown, setCountdown] = useState(15)

  const userToken = useUnit($userToken)!
  const transactionHash = useUnit($transactionHash)!

  const handleFetchStatus = useCallback(async () => {
    setIsFetching(true)
    try {
      const res = await getTransactionStatusFx({ tx_id: transactionHash, token: userToken })
      if (res !== 'PENDING') {
        setStatus(res)
      }
    } catch (e) {
      console.error(e)
    } finally {
      setTimeout(() => {
        setIsFetching(false)
        setCountdown(15)
      }, 2000)
    }
  }, [transactionHash, userToken])

  useEffect(() => {
    handleFetchStatus()
  }, [handleFetchStatus])

  useEffect(() => {
    if (status !== 'PENDING' || isFetching) return

    setCountdown((c) => (c > 15 ? 15 : c))

    const id = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(id)
          return 0
        }
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(id)
  }, [status, isFetching])

  useEffect(() => {
    if (countdown === 0 && status === 'PENDING' && !isFetching) {
      handleFetchStatus()
    }
  }, [countdown, status, isFetching, handleFetchStatus])

  const heading = isFetching ? texts.headings.fetching : texts.headings[status]
  const description = isFetching ? texts.descriptions.fetching : texts.descriptions[status]

  return (
    <>
      <div
        className={classes.root}
        data-status={status}
      >
        {status === 'PENDING' && !isFetching ? (
          <h2
            className={classes.heading}
            style={{ color: 'rgba(30, 255, 0, 1)' }}
          >
            0:{padZero(countdown)}
          </h2>
        ) : (
          <RenderImageByStatus status={status} />
        )}

        <h2 className={classes.heading}>{heading}</h2>
        <span className={classes.description}>{description}</span>

        <ButtonWrapper style={{ padding: '0px 12px' }}>
          <AppButton
            fullWidth
            onClick={() => goToMain()}
            className={classes.button}
          >
            {btnTexts.close}
          </AppButton>
        </ButtonWrapper>
      </div>
      <div
        data-status={status}
        className={classes.glow}
      ></div>
    </>
  )
}

const RenderImageByStatus = ({ status }: { status: TransactionStatus }) => {
  switch (status) {
    case 'PAID':
      return (
        <Icon
          view="thumbsUp"
          style={{ width: '48px' }}
        />
      )
    case 'FAILED':
      return (
        <Icon
          view="thumbsDown"
          style={{ width: '48px' }}
        />
      )
    case 'PENDING':
      return <Spiner variant="secondary" />
  }
}
